import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  getClass,
  updateClass,
  getStudents,
  getAttendanceGrid,
  getAssignments,
  getSubmissions,
  getStudentAnswers,
  createStudent,
  updateStudent,
  addStudentToClass,
  removeStudentFromClass,
  createSession,
  updateCell,
  updateSession,
  generateLessons,
  deleteSession,
  createAssignment,
  updateAssignment,
  deleteAssignment,
  createSubmission,
  getCurriculum,
  replaceCurriculum,
  scanTopics,
  CurriculumItem,
  ClassOut,
  Student,
  AttendanceGrid,
  GridColumn,
  Assignment,
  Submission,
} from "../api/client";
import api from "../api/client";

const STATUSES = ["IP", "CU", "X", ""] as const;

function cleanTitle(title: string): string {
  return title.replace(/^\[Intro\]\s*/, "").replace(/^\[Intermediate\]\s*/, "").replace(/^\[Advanced\]\s*/, "");
}
const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  IP: { bg: "var(--green-light)", color: "var(--green-text)" },
  CU: { bg: "var(--amber-light)", color: "var(--amber-text)" },
  X: { bg: "var(--red-light)", color: "var(--red-text)" },
  "": { bg: "transparent", color: "var(--text-muted)" },
};

function groupByMonth(columns: GridColumn[]): { month: string; cols: GridColumn[] }[] {
  const groups: { month: string; cols: GridColumn[] }[] = [];
  let current: { month: string; cols: GridColumn[] } | null = null;
  for (const col of columns) {
    if (!current || current.month !== col.month) {
      current = { month: col.month, cols: [] };
      groups.push(current);
    }
    current.cols.push(col);
  }
  return groups;
}

function ViewAssignmentModal({ assignment, classId, students, onClose }: {
  assignment: any;
  classId: string;
  students: { id: string; name: string }[];
  onClose: () => void;
}) {
  const [allAnswers, setAllAnswers] = useState<Record<string, Record<string, { answer_text: string; original_text?: string; submitted_at: string }>>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      const result: typeof allAnswers = {};
      for (const s of students) {
        try {
          const r = await getStudentAnswers(classId, s.id);
          result[s.id] = r.data;
        } catch { result[s.id] = {}; }
      }
      setAllAnswers(result);
      setLoading(false);
    };
    load();
  }, [classId, students]);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 700, maxHeight: "90vh", overflow: "auto" }}>
        <h3>{assignment.title}</h3>
        {assignment.description && <p style={{ color: "var(--text-secondary)", marginBottom: 12 }}>{assignment.description}</p>}
        {assignment.due_date && <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 16 }}>Due: {assignment.due_date}</p>}

        {loading ? (
          <div style={{ textAlign: "center", padding: 24, color: "var(--text-muted)" }}>Loading student answers...</div>
        ) : (
          assignment.questions?.map((q: any, i: number) => (
            <div key={q.id} style={{ marginBottom: 20 }}>
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 8, padding: "8px 12px", background: "var(--bg)", borderRadius: 8 }}>
                Q{i + 1}. {q.question_text}
                {q.answer_text && (
                  <div style={{ fontWeight: 400, fontSize: 12, color: "var(--accent-text)", marginTop: 4 }}>
                    Expected: {q.answer_text}
                  </div>
                )}
              </div>
              <div style={{ paddingLeft: 12 }}>
                {students.map((s) => {
                  const ans = allAnswers[s.id]?.[q.id];
                  return (
                    <div key={s.id} style={{ display: "flex", gap: 8, alignItems: "flex-start", padding: "6px 0", borderBottom: "1px solid var(--border-light)", fontSize: 13 }}>
                      <span style={{ fontWeight: 500, minWidth: 100, color: "var(--text-primary)" }}>{s.name}</span>
                      {ans ? (
                        <div style={{ flex: 1 }}>
                          <div>{ans.answer_text}</div>
                          {ans.original_text && ans.original_text !== ans.answer_text && (
                            <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2, fontStyle: "italic" }}>
                              Bahasa: {ans.original_text}
                            </div>
                          )}
                          <span style={{ fontSize: 10, color: "var(--text-muted)" }}>
                            {new Date(ans.submitted_at).toLocaleDateString()}
                          </span>
                        </div>
                      ) : (
                        <span style={{ color: "var(--text-muted)", fontStyle: "italic" }}>Not submitted</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}

        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

export default function ClassDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [notFound, setNotFound] = useState(false);
  const [cls, setCls] = useState<ClassOut | null>(null);
  const [allStudents, setAllStudents] = useState<Student[]>([]);
  const [grid, setGrid] = useState<AttendanceGrid | null>(null);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [curriculum, setCurriculum] = useState<CurriculumItem[]>([]);
  const [tab, setTab] = useState<"students" | "attendance" | "assignments" | "questions" | "fruit" | "settings">("students");
  const [classQuestions, setClassQuestions] = useState<any[]>([]);
  const [fruitData, setFruitData] = useState<any[]>([]);
  const [telegramForm, setTelegramForm] = useState({ chat_id: "", hw_topic_id: "", reminder_topic_id: "" });
  const [telegramSaved, setTelegramSaved] = useState(false);

  // Modals
  const [showAddStudent, setShowAddStudent] = useState(false);
  const [selectedStudentId, setSelectedStudentId] = useState("");
  const [showCreateStudent, setShowCreateStudent] = useState(false);
  const [newStudentForm, setNewStudentForm] = useState({ name: "", phone: "", email: "" });
  const [showAddLesson, setShowAddLesson] = useState(false);
  const [newDate, setNewDate] = useState(new Date().toISOString().split("T")[0]);
  const [newTopic, setNewTopic] = useState("");
  const [newLessonNum, setNewLessonNum] = useState("");
  const [addLessonCurrId, setAddLessonCurrId] = useState("");  // curriculum item id or "__special__"
  const [editLessonCurrId, setEditLessonCurrId] = useState("");
  const [showGenerate, setShowGenerate] = useState(false);
  const [genStart, setGenStart] = useState(new Date().toISOString().split("T")[0]);
  const [genEnd, setGenEnd] = useState("");
  const [genStartNum, setGenStartNum] = useState("1");
  const [genResult, setGenResult] = useState("");
  const [editLesson, setEditLesson] = useState<GridColumn | null>(null);
  const [editForm, setEditForm] = useState({ date: "", topic: "", lesson_number: "" });
  const [editLessonMode, setEditLessonMode] = useState<"curriculum" | "special" | "custom">("curriculum");
  const [showAddAssignment, setShowAddAssignment] = useState(false);
  const [assignForm, setAssignForm] = useState({ title: "", description: "", due_date: "" });
  const [assignQuestions, setAssignQuestions] = useState<{ question_text: string; answer_text: string }[]>([{ question_text: "", answer_text: "" }]);
  const [showSubmitModal, setShowSubmitModal] = useState<string | null>(null);
  const [submitForm, setSubmitForm] = useState({ student_id: "", text_content: "", files: null as FileList | null });
  const [viewAssignment, setViewAssignment] = useState<any>(null);
  const [editAssignment, setEditAssignment] = useState<any>(null);
  const [fruitModal, setFruitModal] = useState<any>(null);
  const [reportModal, setReportModal] = useState<any>(null); // {session_id, date, topic, hasReport}
  const [reportNotes, setReportNotes] = useState("");
  const [reportRatings, setReportRatings] = useState<Record<string, {rating: string, notes: string}>>({});
  const [reportExists, setReportExists] = useState(false);
  const [fruitMeetings, setFruitMeetings] = useState<any[]>([]);
  const [fruitFollowups, setFruitFollowups] = useState<any[]>([]);
  const [fruitQA, setFruitQA] = useState<any[]>([]);
  const [fruitTab, setFruitTab] = useState<"dashboard" | "timeline" | "meetings" | "followups" | "qa" | "reviews">("dashboard");
  const [fruitReviews, setFruitReviews] = useState<any[]>([]);
  const [fruitTimeline, setFruitTimeline] = useState<any[]>([]);
  const [timelineFilter, setTimelineFilter] = useState("all");
  const [timelineSort, setTimelineSort] = useState<"newest" | "oldest">("newest");
  const [fruitForm, setFruitForm] = useState<any>({});
  const [editAssignForm, setEditAssignForm] = useState({ title: "", description: "", due_date: "" });
  const [editAssignQuestions, setEditAssignQuestions] = useState<{ id?: string; question_text: string; answer_text: string }[]>([]);

  const loadClass = useCallback(() => {
    if (!id) return;
    getClass(id).then((r) => {
      setCls(r.data);
      setTelegramForm({
        chat_id: r.data.telegram_chat_id || "",
        hw_topic_id: r.data.telegram_hw_topic_id || "",
        reminder_topic_id: r.data.telegram_reminder_topic_id || "",
      });
    }).catch(() => setNotFound(true));
    getStudents().then((r) => setAllStudents(r.data));
  }, [id]);

  const loadGrid = useCallback(() => {
    if (!id) return;
    getAttendanceGrid(id).then((r) => setGrid(r.data)).catch(() => {});
  }, [id]);

  const loadAssignments = useCallback(() => {
    if (!id) return;
    getAssignments(id).then((r) => setAssignments(r.data)).catch(() => {});
    getSubmissions(undefined, undefined).then((r) => setSubmissions(r.data)).catch(() => {});
  }, [id]);

  const loadCurriculum = useCallback(async () => {
    if (!id) return;
    try {
      const r = await getCurriculum(id);
      if (r.data.length === 0) {
        // Auto-load full curriculum if empty
        const { FULL_CURRICULUM } = await import("../data/lessonPresets");
        await replaceCurriculum(id, FULL_CURRICULUM);
        const r2 = await getCurriculum(id);
        setCurriculum(r2.data);
      } else {
        setCurriculum(r.data);
      }
    } catch {}
  }, [id]);

  const loadFruit = useCallback(() => {
    if (!id) return;
    api.get(`/fruit/summary/class/${id}`).then((r) => setFruitData(r.data)).catch(() => {});
  }, [id]);

  useEffect(() => { loadClass(); loadGrid(); loadAssignments(); loadCurriculum(); loadFruit(); }, [loadClass, loadGrid, loadAssignments, loadCurriculum, loadFruit]);

  // --- Students ---
  const enrolledIds = new Set(cls?.students?.map((s) => s.id) || []);
  const availableStudents = allStudents.filter((s) => !enrolledIds.has(s.id));

  const handleAddStudent = async () => {
    if (!id || !selectedStudentId) return;
    await addStudentToClass(id, selectedStudentId);
    setSelectedStudentId("");
    setShowAddStudent(false);
    loadClass();
    loadGrid();
  };

  const handleCreateAndAddStudent = async () => {
    if (!id || !newStudentForm.name.trim()) return;
    const r = await createStudent(newStudentForm);
    await addStudentToClass(id, r.data.id);
    setNewStudentForm({ name: "", phone: "", email: "" });
    setShowCreateStudent(false);
    setShowAddStudent(false);
    loadClass();
    loadGrid();
  };

  const handleRemoveStudent = async (studentId: string, name: string) => {
    if (!id || !window.confirm(`Remove ${name} from this class?`)) return;
    await removeStudentFromClass(id, studentId);
    loadClass();
    loadGrid();
  };

  // --- Attendance ---
  const handleCellClick = async (sessionId: string, studentId: string, currentStatus: string) => {
    const idx = STATUSES.indexOf(currentStatus as any);
    const next = STATUSES[(idx + 1) % STATUSES.length];
    setGrid((prev) => {
      if (!prev) return prev;
      const newGrid = { ...prev, grid: { ...prev.grid } };
      newGrid.grid[studentId] = { ...newGrid.grid[studentId], [sessionId]: next };
      return newGrid;
    });
    await updateCell({ session_id: sessionId, student_id: studentId, status: next });
  };

  const handleAddLesson = async () => {
    if (!id || !newDate) return;
    let topic = newTopic || undefined;
    let lessonNum = newLessonNum ? parseInt(newLessonNum) : undefined;
    if (addLessonCurrId === "__special__" && newTopic) {
      topic = `[Special Lesson] ${newTopic}`;
      lessonNum = 0;
    }
    try {
      await createSession({
        class_id: id,
        date: newDate,
        lesson_number: lessonNum,
        topic,
        attendances: [],
      });
      setShowAddLesson(false);
      setNewTopic("");
      setNewLessonNum("");
      setAddLessonCurrId("");
      loadGrid();
    } catch (e: any) {
      alert("Failed to add lesson: " + (e.response?.data?.detail || e.message));
    }
  };

  const handleDeleteLesson = async (sessionId: string, date: string) => {
    if (!window.confirm(`Delete lesson on ${date}?`)) return;
    await deleteSession(sessionId);
    loadGrid();
  };

  const handleGenerate = async () => {
    if (!id || !genStart || !genEnd) return;
    try {
      const r = await generateLessons({
        class_id: id,
        start_date: genStart,
        end_date: genEnd,
        start_lesson_number: parseInt(genStartNum) || 1,
      });
      setGenResult(`Created ${r.data.created} lessons (${r.data.parsed_days.join(", ")})`);
      loadGrid();
    } catch (e: any) {
      setGenResult(e.response?.data?.detail || "Error generating lessons");
    }
  };

  const handleEditLesson = async () => {
    if (!editLesson) return;
    let topic = editForm.topic || undefined;
    let lessonNum = editForm.lesson_number ? parseInt(editForm.lesson_number) : undefined;
    if (editLessonCurrId === "__special__" && editForm.topic) {
      topic = `[Special Lesson] ${editForm.topic}`;
      lessonNum = 0;
    }
    await updateSession(editLesson.id, { date: editForm.date || undefined, topic, lesson_number: lessonNum });
    setEditLesson(null);
    loadGrid();
  };

  // --- Assignments ---
  const handleCreateAssignment = async () => {
    if (!id || !assignForm.title) return;
    const questions = assignQuestions.filter((q) => q.question_text.trim());
    await createAssignment({ class_id: id, ...assignForm, questions } as any);
    setAssignForm({ title: "", description: "", due_date: "" });
    setAssignQuestions([{ question_text: "", answer_text: "" }]);
    setShowAddAssignment(false);
    loadAssignments();
  };

  const handleDeleteAssignment = async (assignmentId: string) => {
    if (!window.confirm("Delete this assignment?")) return;
    await deleteAssignment(assignmentId);
    loadAssignments();
  };

  const handleSubmit = async () => {
    if (!showSubmitModal || !submitForm.student_id) return;
    const fd = new FormData();
    fd.append("assignment_id", showSubmitModal);
    fd.append("student_id", submitForm.student_id);
    fd.append("source", "teacher");
    if (submitForm.text_content) fd.append("text_content", submitForm.text_content);
    if (submitForm.files) {
      for (let i = 0; i < submitForm.files.length; i++) {
        fd.append("files", submitForm.files[i]);
      }
    }
    await createSubmission(fd);
    setShowSubmitModal(null);
    setSubmitForm({ student_id: "", text_content: "", files: null });
    loadAssignments();
  };

  const submissionCount = (assignmentId: string) =>
    submissions.filter((s) => s.assignment_id === assignmentId).length;

  const monthGroups = grid ? groupByMonth(grid.columns) : [];

  if (notFound) return (
    <div className="empty">
      Class not found. <Link to="/classes" style={{ color: "var(--accent)" }}>Go to Classes</Link>
    </div>
  );
  if (!cls) return <div className="empty">Loading...</div>;

  return (
    <div>
      <div className="detail-header">
        <div>
          <Link to="/classes" style={{ color: "var(--text-muted)", fontSize: 13 }}>&larr; Back to Classes</Link>
          <h2
            contentEditable
            suppressContentEditableWarning
            onBlur={async (e) => {
              const newName = e.currentTarget.textContent?.trim();
              if (newName && newName !== cls.name && id) {
                await updateClass(id, { name: newName });
                loadClass();
              }
            }}
            style={{ cursor: "text", outline: "none", borderBottom: "1px dashed transparent" }}
            onFocus={(e) => { e.currentTarget.style.borderBottom = "1px dashed var(--accent)"; }}
            onMouseOver={(e) => { e.currentTarget.style.borderBottom = "1px dashed var(--border)"; }}
            onMouseOut={(e) => { if (document.activeElement !== e.currentTarget) e.currentTarget.style.borderBottom = "1px dashed transparent"; }}
          >{cls.name}</h2>
          <div style={{ color: "var(--text-muted)", fontSize: 14, display: "flex", alignItems: "center", gap: 12 }}>
            <span>
              {cls.schedule && (
                <span
                  contentEditable
                  suppressContentEditableWarning
                  style={{ cursor: "text", outline: "none", borderBottom: "1px dashed transparent" }}
                  onFocus={(e) => { e.currentTarget.style.borderBottom = "1px dashed var(--accent)"; }}
                  onBlur={async (e) => {
                    e.currentTarget.style.borderBottom = "1px dashed transparent";
                    const val = e.currentTarget.textContent?.trim();
                    if (val && val !== cls.schedule && id) {
                      await updateClass(id, { schedule: val });
                      loadClass();
                    }
                  }}
                >{cls.schedule}</span>
              )}
              {cls.schedule && <span> &middot; </span>}
              {cls.student_count} students
            </span>
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => {
                const url = `${window.location.origin}/hw/${id}`;
                navigator.clipboard.writeText(url);
                alert("Homework link copied!\n" + url);
              }}
            >Copy Homework Link</button>
          </div>
        </div>
      </div>

      <div className="tab-bar">
        <button className={tab === "students" ? "active" : ""} onClick={() => setTab("students")}>
          Students ({cls.students?.length || 0})
        </button>
        <button className={tab === "attendance" ? "active" : ""} onClick={() => setTab("attendance")}>
          Attendance ({grid?.columns.length || 0} lessons)
        </button>
        <button className={tab === "assignments" ? "active" : ""} onClick={() => setTab("assignments")}>
          Assignments ({assignments.length})
        </button>
        <button className={tab === "questions" ? "active" : ""} onClick={async () => {
          setTab("questions");
          if (id) { const r = await api.get(`/fruit/questions/class/${id}`); setClassQuestions(r.data); }
        }}>
          Questions
        </button>
        <button className={tab === "settings" ? "active" : ""} onClick={() => setTab("settings")}>
          Settings
        </button>
      </div>

      {/* ===== STUDENTS TAB ===== */}
      {tab === "students" && (
        <>
          <div style={{ marginBottom: 12 }}>
            <button className="btn btn-primary btn-sm" onClick={() => setShowAddStudent(true)}>+ Add Student</button>
          </div>
          <div className="table-container">
            {(!cls.students || cls.students.length === 0) ? (
              <div className="empty">No students enrolled. Add students to start tracking attendance.</div>
            ) : (
              <table>
                <thead><tr><th>#</th><th>Name</th><th>Attendance</th><th>HW</th><th>Last Meeting</th><th>F/U</th><th></th></tr></thead>
                <tbody>
                  {cls.students.map((s: any, i: number) => {
                    const fruit = fruitData.find((f: any) => f.id === s.id);
                    return (
                      <tr key={s.id}>
                        <td style={{ color: "var(--text-muted)", width: 30 }}>{i + 1}</td>
                        <td>
                          <span
                            style={{ color: "var(--accent)", fontWeight: 500, cursor: "pointer" }}
                            onClick={async () => {
                              setFruitModal(s);
                              setFruitTab("dashboard");
                              const [m, f, q, rv, tl] = await Promise.all([
                                api.get(`/fruit/${s.id}/meetings`),
                                api.get(`/fruit/${s.id}/followups`),
                                api.get(`/fruit/${s.id}/qa`),
                                api.get(`/fruit/${s.id}/reviews`),
                                api.get(`/fruit/${s.id}/timeline`),
                              ]);
                              setFruitMeetings(m.data);
                              setFruitFollowups(f.data);
                              setFruitQA(q.data);
                              setFruitReviews(rv.data);
                              setFruitTimeline(tl.data);
                              setTimelineFilter("all");
                              setTimelineSort("newest");
                            }}
                          >{s.name}</span>
                        </td>
                        <td>
                          {fruit ? (
                            <span className={`badge ${fruit.attendance_pct >= 80 ? "present" : fruit.attendance_pct >= 50 ? "late" : "absent"}`}>
                              {fruit.attendance_pct}%
                            </span>
                          ) : "-"}
                        </td>
                        <td>{fruit ? `${fruit.hw_done || 0}/${fruit.hw_total || 0}` : "-"}</td>
                        <td style={{ fontSize: 12 }}>{fruit?.last_meeting || <span style={{ color: "var(--text-muted)" }}>-</span>}</td>
                        <td>
                          {fruit?.pending_followups > 0 ? (
                            <span className="badge pending">{fruit.pending_followups}</span>
                          ) : <span style={{ color: "var(--text-muted)" }}>0</span>}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          <button className="btn btn-danger btn-sm" onClick={() => handleRemoveStudent(s.id, s.name)}>Remove</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {showAddStudent && !showCreateStudent && (
            <div className="modal-overlay" onClick={() => setShowAddStudent(false)}>
              <div className="modal" onClick={(e) => e.stopPropagation()}>
                <h3>Add Student to Class</h3>
                {availableStudents.length > 0 && (
                  <>
                    <div className="form-group">
                      <label>Existing Student</label>
                      <select value={selectedStudentId} onChange={(e) => setSelectedStudentId(e.target.value)}>
                        <option value="">Select...</option>
                        {availableStudents.map((s) => (
                          <option key={s.id} value={s.id}>{s.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="modal-actions" style={{ marginBottom: 0 }}>
                      <button className="btn btn-secondary" onClick={() => setShowAddStudent(false)}>Cancel</button>
                      <button className="btn btn-primary" onClick={handleAddStudent}>Add</button>
                    </div>
                    <div style={{ textAlign: "center", color: "var(--text-muted)", fontSize: 13, margin: "16px 0 8px", borderTop: "1px solid var(--border-light)", paddingTop: 16 }}>or</div>
                  </>
                )}
                <button
                  className="btn btn-secondary"
                  style={{ width: "100%" }}
                  onClick={() => setShowCreateStudent(true)}
                >
                  Create New Student
                </button>
              </div>
            </div>
          )}

          {showCreateStudent && (
            <div className="modal-overlay" onClick={() => { setShowCreateStudent(false); setShowAddStudent(false); }}>
              <div className="modal" onClick={(e) => e.stopPropagation()}>
                <h3>Create & Enroll Student</h3>
                <div className="form-group">
                  <label>Name *</label>
                  <input value={newStudentForm.name} onChange={(e) => setNewStudentForm({ ...newStudentForm, name: e.target.value })} />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Phone</label>
                    <input value={newStudentForm.phone} onChange={(e) => setNewStudentForm({ ...newStudentForm, phone: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Email</label>
                    <input value={newStudentForm.email} onChange={(e) => setNewStudentForm({ ...newStudentForm, email: e.target.value })} />
                  </div>
                </div>
                <div className="modal-actions">
                  <button className="btn btn-secondary" onClick={() => setShowCreateStudent(false)}>Back</button>
                  <button className="btn btn-primary" onClick={handleCreateAndAddStudent}>Create & Add</button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* ===== ATTENDANCE TAB ===== */}
      {tab === "attendance" && (
        <>
          <div style={{ marginBottom: 12 }}>
            <button className="btn btn-primary btn-sm" onClick={() => setShowAddLesson(true)}>+ Add Lesson</button>
            <div style={{ display: "flex", gap: 12, fontSize: 11, marginTop: 10 }}>
              {[
                { code: "IP", label: "In Person", bg: "var(--green-light)", color: "var(--green-text)" },
                { code: "CU", label: "Catch Up", bg: "var(--amber-light)", color: "var(--amber-text)" },
                { code: "X", label: "Absent", bg: "var(--red-light)", color: "var(--red-text)" },
              ].map((s) => (
                <div key={s.code} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <span style={{
                    display: "inline-block", width: 24, height: 18, borderRadius: 3,
                    background: s.bg, color: s.color, textAlign: "center", lineHeight: "18px",
                    fontWeight: 600, fontSize: 10,
                  }}>{s.code}</span>
                  <span style={{ color: "var(--text-secondary)" }}>{s.label}</span>
                </div>
              ))}
            </div>
          </div>

          {(!grid || grid.students.length === 0) ? (
            <div className="empty">Add students to this class first to start recording attendance.</div>
          ) : grid.columns.length === 0 ? (
            <div className="empty">No lessons recorded yet. Click "+ Add Lesson" to create the first column.</div>
          ) : (
            <>
              <div style={{ overflowX: "auto", border: "1px solid var(--border)", borderRadius: "var(--radius)" }}>
                <table style={{ width: "auto", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead>
                    {/* Month row */}
                    <tr>
                      <th style={{ position: "sticky", left: 0, zIndex: 3, background: "#f5f5f4", padding: "4px 8px", width: 30, textAlign: "center", borderRight: "1px solid var(--border)" }}>#</th>
                      <th style={{ position: "sticky", left: 30, zIndex: 3, background: "#f5f5f4", padding: "4px 10px", borderRight: "2px solid var(--border)" }}>Name</th>
                      {monthGroups.map((mg) => (
                        <th
                          key={mg.month}
                          colSpan={mg.cols.length}
                          style={{
                            textAlign: "center", background: "var(--accent)", color: "#fff",
                            fontWeight: 600, fontSize: 11, padding: "4px 0",
                            borderRight: "1px solid rgba(255,255,255,0.3)",
                          }}
                        >{mg.month}</th>
                      ))}
                    </tr>
                    {/* Day + date number row */}
                    <tr>
                      <th style={{ position: "sticky", left: 0, zIndex: 3, background: "#f5f5f4", borderRight: "1px solid var(--border)" }}></th>
                      <th style={{ position: "sticky", left: 30, zIndex: 3, background: "#f5f5f4", borderRight: "2px solid var(--border)" }}></th>
                      {grid.columns.map((col) => (
                        <th
                          key={col.id}
                          style={{
                            textAlign: "center", padding: "3px 0", minWidth: 28, width: 28,
                            fontSize: 10, fontWeight: 600, background: "#f5f5f4",
                            borderRight: "1px solid var(--border-light)",
                          }}
                          title={`${col.day} ${col.day_num} — ${col.topic || col.date}`}
                        >
                          {col.day_num}
                        </th>
                      ))}
                    </tr>
                    {/* Lesson title row (vertical text like Excel) */}
                    <tr style={{ borderBottom: "2px solid var(--border)" }}>
                      <th style={{ position: "sticky", left: 0, zIndex: 3, background: "#f5f5f4", borderRight: "1px solid var(--border)" }}></th>
                      <th style={{ position: "sticky", left: 30, zIndex: 3, background: "#f5f5f4", padding: "2px 10px", fontSize: 9, color: "var(--text-muted)", borderRight: "2px solid var(--border)", verticalAlign: "bottom" }}>Topic</th>
                      {grid.columns.map((col) => (
                        <th
                          key={col.id}
                          style={{
                            background: "#f5f5f4",
                            borderRight: "1px solid var(--border-light)",
                            padding: 0,
                            height: col.topic ? 120 : 24,
                            verticalAlign: "bottom",
                            cursor: "pointer",
                            position: "relative",
                          }}
                          title={`${col.topic || "No topic"} (${col.date}) — click to edit`}
                          onClick={() => {
                            setEditLesson(col);
                            setEditForm({ date: col.date, topic: cleanTitle(col.topic || ""), lesson_number: col.lesson_number?.toString() || "" });
                            // Match to curriculum item
                            const match = curriculum.find((c) => c.lesson_number === col.lesson_number);
                            setEditLessonCurrId(col.topic?.startsWith("[Special Lesson]") ? "__special__" : match?.id || "");
                          }}
                        >
                          <div style={{
                            writingMode: "vertical-rl",
                            transform: "rotate(180deg)",
                            fontSize: 8,
                            fontWeight: 400,
                            color: col.topic ? "var(--text-secondary)" : "var(--text-muted)",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            maxHeight: 115,
                            padding: "2px 0",
                            margin: "0 auto",
                            textAlign: "left",
                          }}>
                            {col.topic
                              ? (col.lesson_number ? `[L${col.lesson_number}] ${col.topic}` : col.topic)
                              : (col.lesson_number ? `L${col.lesson_number}` : "·")}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {grid.students.map((student, idx) => (
                      <tr key={student.id} style={{ borderBottom: "1px solid var(--border-light)" }}>
                        <td style={{
                          position: "sticky", left: 0, zIndex: 2, background: "#fff",
                          fontWeight: 500, fontSize: 11, padding: "0 8px", color: "var(--text-muted)",
                          textAlign: "center", borderRight: "1px solid var(--border)",
                          height: 32,
                        }}>{idx + 1}</td>
                        <td style={{
                          position: "sticky", left: 30, zIndex: 2, background: "#fff",
                          fontWeight: 500, fontSize: 12, padding: "0 10px", whiteSpace: "nowrap",
                          borderRight: "2px solid var(--border)",
                          height: 32,
                        }}>{student.name}</td>
                        {grid.columns.map((col) => {
                          const status = grid.grid[student.id]?.[col.id] || "";
                          const colors = STATUS_COLORS[status] || STATUS_COLORS[""];
                          return (
                            <td
                              key={col.id}
                              onClick={() => handleCellClick(col.id, student.id, status)}
                              style={{
                                textAlign: "center", padding: 0, cursor: "pointer",
                                borderRight: "1px solid var(--border-light)",
                                width: 28, height: 28, userSelect: "none",
                                background: status ? colors.bg : "transparent",
                              }}
                            >
                              <span style={{
                                fontWeight: 700, fontSize: 10,
                                color: status ? colors.color : "var(--border)",
                                lineHeight: "28px",
                              }}>{status || "\u00B7"}</span>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div style={{ marginTop: 10, fontSize: 11, color: "var(--text-muted)", display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
                <span>Click cell to cycle: IP → CU → X → clear &middot; {grid.columns.length} lessons</span>
              </div>
            </>
          )}

          {showAddLesson && (
            <div className="modal-overlay" onClick={() => setShowAddLesson(false)}>
              <div className="modal" onClick={(e) => e.stopPropagation()}>
                <h3>Add Lesson</h3>
                <div className="form-group">
                  <label>Date *</label>
                  <input type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Curriculum Lesson</label>
                  <select
                    value={addLessonCurrId}
                    onChange={(e) => {
                      const val = e.target.value;
                      setAddLessonCurrId(val);
                      if (val === "__special__") {
                        setNewLessonNum("0");
                        setNewTopic("");
                      } else {
                        const item = curriculum.find((c) => c.id === val);
                        if (item) {
                          setNewLessonNum(item.lesson_number.toString());
                          setNewTopic(cleanTitle(item.title));
                        }
                      }
                    }}
                  >
                    <option value="">Select official lesson...</option>
                    {(() => {
                      const intro = curriculum.filter((c) => c.title.startsWith("[Intro]"));
                      const inter = curriculum.filter((c) => c.title.startsWith("[Intermediate]"));
                      const adv = curriculum.filter((c) => c.title.startsWith("[Advanced]"));
                      return (
                        <>
                          {intro.length > 0 && <optgroup label="Introductory">{intro.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}</optgroup>}
                          {inter.length > 0 && <optgroup label="Intermediate">{inter.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}</optgroup>}
                          {adv.length > 0 && <optgroup label="Advanced / Revelation">{adv.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}</optgroup>}
                        </>
                      );
                    })()}
                    <option value="__special__">Special Lesson (not in official list)</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Lesson Title</label>
                  <input value={newTopic} onChange={(e) => setNewTopic(e.target.value)} placeholder="e.g. 1-1 Consultation, Recap of Figurative Fire" />
                </div>
                <div className="modal-actions">
                  <button className="btn btn-secondary" onClick={() => setShowAddLesson(false)}>Cancel</button>
                  <button className="btn btn-primary" onClick={handleAddLesson}>Add</button>
                </div>
              </div>
            </div>
          )}

          {/* Generate Lessons Modal */}
          {showGenerate && (
            <div className="modal-overlay" onClick={() => setShowGenerate(false)}>
              <div className="modal" onClick={(e) => e.stopPropagation()}>
                <h3>Generate Lessons from Schedule</h3>
                <p style={{ fontSize: 13, color: "var(--text-secondary)", marginBottom: 16 }}>
                  Schedule: <strong>{cls.schedule || "Not set"}</strong>
                  {!cls.schedule && <span style={{ color: "var(--red)" }}> — edit the class to set a schedule first</span>}
                </p>
                <div className="form-row">
                  <div className="form-group">
                    <label>Start Date *</label>
                    <input type="date" value={genStart} onChange={(e) => setGenStart(e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label>End Date *</label>
                    <input type="date" value={genEnd} onChange={(e) => setGenEnd(e.target.value)} />
                  </div>
                </div>
                <div className="form-group">
                  <label>Start Lesson # from</label>
                  <input type="number" value={genStartNum} onChange={(e) => setGenStartNum(e.target.value)} />
                </div>
                {genResult && (
                  <div style={{ padding: 12, borderRadius: 8, background: genResult.startsWith("Created") ? "var(--green-light)" : "var(--red-light)", fontSize: 13, marginBottom: 12 }}>
                    {genResult}
                  </div>
                )}
                <div className="modal-actions">
                  <button className="btn btn-secondary" onClick={() => setShowGenerate(false)}>Close</button>
                  <button className="btn btn-primary" onClick={handleGenerate} disabled={!cls.schedule}>Generate</button>
                </div>
              </div>
            </div>
          )}

          {/* Edit Lesson Modal */}
          {editLesson && (
            <div className="modal-overlay" onClick={() => setEditLesson(null)}>
              <div className="modal" onClick={(e) => e.stopPropagation()}>
                <h3>Edit Lesson</h3>
                <div className="form-group">
                  <label>Date</label>
                  <input type="date" value={editForm.date} onChange={(e) => setEditForm({ ...editForm, date: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Curriculum Lesson</label>
                  <select
                    value={editLessonCurrId}
                    onChange={(e) => {
                      const val = e.target.value;
                      setEditLessonCurrId(val);
                      if (val === "__special__") {
                        setEditForm({ ...editForm, lesson_number: "0", topic: "" });
                      } else {
                        const item = curriculum.find((c) => c.id === val);
                        if (item) {
                          setEditForm({ ...editForm, lesson_number: item.lesson_number.toString(), topic: cleanTitle(item.title) });
                        }
                      }
                    }}
                  >
                    <option value="">Select official lesson...</option>
                    {(() => {
                      const intro = curriculum.filter((c) => c.title.startsWith("[Intro]"));
                      const inter = curriculum.filter((c) => c.title.startsWith("[Intermediate]"));
                      const adv = curriculum.filter((c) => c.title.startsWith("[Advanced]"));
                      return (
                        <>
                          {intro.length > 0 && <optgroup label="Introductory">{intro.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}</optgroup>}
                          {inter.length > 0 && <optgroup label="Intermediate">{inter.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}</optgroup>}
                          {adv.length > 0 && <optgroup label="Advanced / Revelation">{adv.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}</optgroup>}
                        </>
                      );
                    })()}
                    <option value="__special__">Special Lesson (not in official list)</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Lesson Title</label>
                  <input value={editForm.topic} onChange={(e) => setEditForm({ ...editForm, topic: e.target.value })} placeholder="e.g. 1-1 Consultation" />
                </div>
                <div className="modal-actions">
                  <button className="btn btn-danger" onClick={() => { handleDeleteLesson(editLesson.id, editLesson.date); setEditLesson(null); }}>Delete</button>
                  <button className="btn btn-secondary" onClick={async () => {
                    const r = await api.get(`/reports/session/${editLesson.id}`);
                    const existing = r.data;
                    const hasReport = !!existing?.id;
                    setReportExists(hasReport);
                    setReportNotes(existing?.overall_notes || "");
                    const ratingMap: Record<string, {rating: string, notes: string}> = {};
                    if (existing?.ratings) {
                      for (const rt of existing.ratings) ratingMap[rt.student_id] = { rating: rt.rating, notes: rt.notes || "" };
                    }
                    setReportRatings(ratingMap);
                    setReportModal({ session_id: editLesson.id, date: editLesson.date, topic: editLesson.topic });
                    setEditLesson(null);
                  }}>{reportExists ? "Edit Report" : "Write Report"}</button>
                  <div style={{ flex: 1 }} />
                  <button className="btn btn-secondary" onClick={() => setEditLesson(null)}>Cancel</button>
                  <button className="btn btn-primary" onClick={handleEditLesson}>Save</button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* ===== ASSIGNMENTS TAB ===== */}
      {tab === "assignments" && (
        <>
          <div style={{ marginBottom: 12 }}>
            <button className="btn btn-primary btn-sm" onClick={() => setShowAddAssignment(true)}>+ New Assignment</button>
          </div>

          <div className="table-container">
            {assignments.length === 0 ? (
              <div className="empty">No assignments yet. Create one to start tracking homework.</div>
            ) : (
              <table>
                <thead>
                  <tr><th>Title</th><th>Due Date</th><th>Answers</th><th></th></tr>
                </thead>
                <tbody>
                  {assignments.map((a: any) => (
                    <tr key={a.id}>
                      <td>
                        <span style={{ fontWeight: 500, cursor: "pointer", color: "var(--accent)" }} onClick={() => setViewAssignment(a)}>
                          {a.title}
                        </span>
                        {a.questions?.length > 0 && (
                          <span style={{ fontSize: 11, color: "var(--text-muted)", marginLeft: 8 }}>
                            {a.questions.length} Q&A
                          </span>
                        )}
                      </td>
                      <td>{a.due_date ? `${a.due_date} 2pm` : "-"}</td>
                      <td style={{ cursor: "pointer" }} onClick={() => setViewAssignment(a)}>
                        <span className={`badge ${(a.answered_count || 0) > 0 ? "present" : "pending"}`}>
                          {a.answered_count || 0} / {cls.students?.length || 0}
                        </span>
                        <span style={{ fontSize: 11, color: "var(--accent)", marginLeft: 8 }}>View</span>
                      </td>
                      <td style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                        <button className="btn btn-secondary btn-sm" onClick={() => {
                          setEditAssignment(a);
                          setEditAssignForm({ title: a.title, description: a.description || "", due_date: a.due_date || "" });
                          setEditAssignQuestions((a.questions || []).map((q: any) => ({ id: q.id, question_text: q.question_text, answer_text: q.answer_text || "" })));
                        }}>Edit</button>
                        <button className="btn btn-danger btn-sm" onClick={() => handleDeleteAssignment(a.id)}>Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {showAddAssignment && (
            <div className="modal-overlay" onClick={() => setShowAddAssignment(false)}>
              <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 600 }}>
                <h3>New Assignment</h3>
                <div className="form-group">
                  <label>Title *</label>
                  <input value={assignForm.title} onChange={(e) => setAssignForm({ ...assignForm, title: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea value={assignForm.description} onChange={(e) => setAssignForm({ ...assignForm, description: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Due Date (auto: next class day 2pm)</label>
                  <input type="date" value={assignForm.due_date} onChange={(e) => setAssignForm({ ...assignForm, due_date: e.target.value })} />
                  <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4 }}>Leave empty to auto-set to next class day. Deadline is 2:00 PM.</div>
                </div>

                {/* Questions */}
                <div style={{ marginTop: 20, paddingTop: 16, borderTop: "1px solid var(--border-light)" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: "0.04em" }}>Questions & Answers</label>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => setAssignQuestions([...assignQuestions, { question_text: "", answer_text: "" }])}
                    >+ Add Question</button>
                  </div>
                  {assignQuestions.map((q, i) => (
                    <div key={i} style={{ background: "var(--bg)", borderRadius: 8, padding: 12, marginBottom: 8, position: "relative" }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-muted)", marginBottom: 6 }}>Q{i + 1}</div>
                      <div className="form-group" style={{ marginBottom: 8 }}>
                        <input
                          placeholder="Question"
                          value={q.question_text}
                          onChange={(e) => {
                            const updated = [...assignQuestions];
                            updated[i] = { ...updated[i], question_text: e.target.value };
                            setAssignQuestions(updated);
                          }}
                        />
                      </div>
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <input
                          placeholder="Answer (optional)"
                          value={q.answer_text}
                          onChange={(e) => {
                            const updated = [...assignQuestions];
                            updated[i] = { ...updated[i], answer_text: e.target.value };
                            setAssignQuestions(updated);
                          }}
                        />
                      </div>
                      {assignQuestions.length > 1 && (
                        <button
                          style={{ position: "absolute", top: 8, right: 8, background: "none", border: "none", color: "var(--red)", cursor: "pointer", fontSize: 16 }}
                          onClick={() => setAssignQuestions(assignQuestions.filter((_, j) => j !== i))}
                        >&times;</button>
                      )}
                    </div>
                  ))}
                </div>

                <div className="modal-actions">
                  <button className="btn btn-secondary" onClick={() => setShowAddAssignment(false)}>Cancel</button>
                  <button className="btn btn-primary" onClick={handleCreateAssignment}>Create</button>
                </div>
              </div>
            </div>
          )}

          {/* Edit Assignment Modal */}
          {editAssignment && (
            <div className="modal-overlay" onClick={() => setEditAssignment(null)}>
              <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 600 }}>
                <h3>Edit Assignment</h3>
                <div className="form-group">
                  <label>Title *</label>
                  <input value={editAssignForm.title} onChange={(e) => setEditAssignForm({ ...editAssignForm, title: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea value={editAssignForm.description} onChange={(e) => setEditAssignForm({ ...editAssignForm, description: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Due Date</label>
                  <input type="date" value={editAssignForm.due_date} onChange={(e) => setEditAssignForm({ ...editAssignForm, due_date: e.target.value })} />
                </div>
                <div style={{ marginTop: 20, paddingTop: 16, borderTop: "1px solid var(--border-light)" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: "0.04em" }}>Questions & Answers</label>
                    <button className="btn btn-secondary btn-sm" onClick={() => setEditAssignQuestions([...editAssignQuestions, { question_text: "", answer_text: "" }])}>+ Add Question</button>
                  </div>
                  {editAssignQuestions.map((q, i) => (
                    <div key={i} style={{ background: "var(--bg)", borderRadius: 8, padding: 12, marginBottom: 8, position: "relative" }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-muted)", marginBottom: 6 }}>Q{i + 1}</div>
                      <div className="form-group" style={{ marginBottom: 8 }}>
                        <input placeholder="Question" value={q.question_text} onChange={(e) => {
                          const updated = [...editAssignQuestions];
                          updated[i] = { ...updated[i], question_text: e.target.value };
                          setEditAssignQuestions(updated);
                        }} />
                      </div>
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <input placeholder="Answer (optional)" value={q.answer_text} onChange={(e) => {
                          const updated = [...editAssignQuestions];
                          updated[i] = { ...updated[i], answer_text: e.target.value };
                          setEditAssignQuestions(updated);
                        }} />
                      </div>
                      {editAssignQuestions.length > 1 && (
                        <button style={{ position: "absolute", top: 8, right: 8, background: "none", border: "none", color: "var(--red)", cursor: "pointer", fontSize: 16 }}
                          onClick={() => setEditAssignQuestions(editAssignQuestions.filter((_, j) => j !== i))}>&times;</button>
                      )}
                    </div>
                  ))}
                </div>
                <div className="modal-actions">
                  <button className="btn btn-secondary" onClick={() => setEditAssignment(null)}>Cancel</button>
                  <button className="btn btn-primary" onClick={async () => {
                    if (!editAssignment || !editAssignForm.title) return;
                    const questions = editAssignQuestions.filter((q) => q.question_text.trim());
                    await updateAssignment(editAssignment.id, { class_id: id, ...editAssignForm, questions });
                    setEditAssignment(null);
                    loadAssignments();
                  }}>Save</button>
                </div>
              </div>
            </div>
          )}

          {/* View Assignment Modal with Student Answers */}
          {viewAssignment && (
            <ViewAssignmentModal
              assignment={viewAssignment}
              classId={id!}
              students={cls.students || []}
              onClose={() => setViewAssignment(null)}
            />
          )}

        </>
      )}

      {/* ===== LESSON REPORT MODAL ===== */}
      {reportModal && (
        <div className="modal-overlay" onClick={() => setReportModal(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 650, maxHeight: "90vh", overflow: "auto" }}>
            <h3>Post-Center Report</h3>
            <div style={{ fontSize: 13, color: "var(--text-secondary)", marginBottom: 16 }}>
              {reportModal.date} — {reportModal.topic || "No topic"}
            </div>

            <div className="form-group">
              <label>Overall Notes</label>
              <textarea value={reportNotes} onChange={(e) => setReportNotes(e.target.value)} placeholder="Overall observations about the lesson..." style={{ minHeight: 60 }} />
            </div>

            <div style={{ marginBottom: 8, fontSize: 12, color: "var(--text-muted)" }}>
              Rate each student: <span style={{ color: "#16a34a" }}>Green</span> = Quick &middot;
              <span style={{ color: "#f59e0b" }}> Orange</span> = Average &middot;
              <span style={{ color: "#eab308" }}> Yellow</span> = Might need help &middot;
              <span style={{ color: "#dc2626" }}> Red</span> = Need to check
            </div>

            {cls.students?.map((s: any) => {
              const r = reportRatings[s.id] || { rating: "", notes: "" };
              return (
                <div key={s.id} style={{ display: "flex", gap: 8, alignItems: "flex-start", padding: "8px 0", borderBottom: "1px solid var(--border-light)" }}>
                  <div style={{ minWidth: 90, fontWeight: 500, fontSize: 13, paddingTop: 4 }}>{s.name}</div>
                  <div style={{ display: "flex", gap: 3 }}>
                    {(["green", "orange", "yellow", "red"] as const).map((color) => (
                      <button
                        key={color}
                        onClick={() => setReportRatings({ ...reportRatings, [s.id]: { ...r, rating: r.rating === color ? "" : color } })}
                        style={{
                          width: 22, height: 22, borderRadius: "50%", border: r.rating === color ? "2px solid #1c1917" : "1px solid var(--border)",
                          background: color === "green" ? "#16a34a" : color === "orange" ? "#e67e22" : color === "yellow" ? "#facc15" : "#dc2626",
                          cursor: "pointer", opacity: r.rating === color ? 1 : 0.3,
                        }}
                      />
                    ))}
                  </div>
                  <input
                    value={r.notes}
                    onChange={(e) => setReportRatings({ ...reportRatings, [s.id]: { ...r, notes: e.target.value } })}
                    placeholder="Notes..."
                    style={{ flex: 1, padding: "4px 8px", fontSize: 12, border: "1px solid var(--border)", borderRadius: 6 }}
                  />
                </div>
              );
            })}

            <div className="modal-actions">
              {reportExists && <button className="btn btn-danger" onClick={async () => {
                const r = await api.get(`/reports/session/${reportModal.session_id}`);
                if (r.data?.id) {
                  await api.delete(`/reports/${r.data.id}`);
                  setReportModal(null);
                  setReportExists(false);
                  await loadFruit();
                }
              }}>Delete Report</button>}
              <button className="btn btn-secondary" onClick={() => setReportModal(null)}>Cancel</button>
              <button className="btn btn-secondary" onClick={async () => {
                // Generate and send to Telegram
                const ratings = Object.entries(reportRatings).filter(([_, v]) => v.rating);
                const groups: Record<string, {name: string, notes: string}[]> = { green: [], orange: [], yellow: [], red: [] };
                for (const [sid, v] of ratings) {
                  const student = cls.students?.find((s: any) => s.id === sid);
                  if (student && v.rating) groups[v.rating].push({ name: student.name, notes: v.notes });
                }
                // Get attendance for this session
                const sessionData = await api.get(`/sessions/${reportModal.session_id}`);
                const attendances = sessionData.data?.attendances || [];
                const present = attendances.filter((a: any) => a.status === "IP" || a.status === "CU");
                const absent = attendances.filter((a: any) => a.status === "X");
                const late = attendances.filter((a: any) => a.status === "CU");

                let text = `[Post-Center Report]\n\n`;
                text += `Lesson: ${reportModal.topic || "N/A"}\n`;
                text += `Date: ${reportModal.date}\n`;
                text += `Attendance: ${present.length}/${cls.students?.length || 0}\n`;
                if (absent.length > 0) text += `Absent: ${absent.map((a: any) => cls.students?.find((s: any) => s.id === a.student_id)?.name || "?").join(", ")}\n`;
                text += `\nOverall: ${reportNotes || "-"}\n`;

                const labels: Record<string, {emoji: string, label: string}> = {
                  green: { emoji: "🟢", label: "Quick" },
                  orange: { emoji: "🟠", label: "Average" },
                  yellow: { emoji: "🟡", label: "Might need help" },
                  red: { emoji: "🔴", label: "Need to check" },
                };
                for (const color of ["green", "orange", "yellow", "red"]) {
                  if (groups[color].length > 0) {
                    text += `\n${labels[color].emoji} ${labels[color].label}: ${groups[color].length}\n`;
                    for (const s of groups[color]) {
                      text += `• ${s.name}${s.notes ? ": " + s.notes : ""}\n`;
                    }
                  }
                }

                if (cls.telegram_chat_id && cls.telegram_reports_topic_id) {
                  try {
                    await api.post("/reports/send-telegram", { chat_id: cls.telegram_chat_id, topic_id: cls.telegram_reports_topic_id, text });
                    alert("Report sent to Centre Reports topic!");
                  } catch { alert("Failed to send. Copied to clipboard instead."); navigator.clipboard.writeText(text); }
                } else {
                  navigator.clipboard.writeText(text);
                  alert("Report copied to clipboard! (No Centre Reports topic configured)");
                }
              }}>Send Report</button>
              <button className="btn btn-primary" onClick={async () => {
                const ratings = Object.entries(reportRatings)
                  .filter(([_, v]) => v.rating)
                  .map(([sid, v]) => ({ student_id: sid, rating: v.rating, notes: v.notes }));
                await api.post("/reports", {
                  session_id: reportModal.session_id,
                  class_id: id,
                  overall_notes: reportNotes,
                  ratings,
                });
                setReportModal(null);
                setReportExists(true);
                await loadFruit();
              }}>Save Report</button>
            </div>
          </div>
        </div>
      )}

      {/* ===== FRUIT FILE MODAL ===== */}
      {fruitModal && (
        <div className="modal-overlay" onClick={() => setFruitModal(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 600, maxHeight: "90vh", overflow: "auto" }}>
            <h3>{fruitModal.name}</h3>
            <div className="tab-bar" style={{ marginBottom: 12 }}>
              <button className={fruitTab === "dashboard" ? "active" : ""} onClick={() => setFruitTab("dashboard")}>Dashboard</button>
              <button className={fruitTab === "timeline" ? "active" : ""} onClick={() => setFruitTab("timeline")}>Timeline ({fruitTimeline.length})</button>
              <button className={fruitTab === "meetings" ? "active" : ""} onClick={() => setFruitTab("meetings")}>Meetings ({fruitMeetings.length})</button>
              <button className={fruitTab === "followups" ? "active" : ""} onClick={() => setFruitTab("followups")}>Follow-ups ({fruitFollowups.length})</button>
              <button className={fruitTab === "qa" ? "active" : ""} onClick={() => setFruitTab("qa")}>Q&A ({fruitQA.length})</button>
            </div>

            {fruitTab === "dashboard" && (() => {
              const fruit = fruitData.find((f: any) => f.id === fruitModal.id);
              const ratingValues: Record<string, number> = { green: 4, orange: 3, yellow: 2, red: 1 };
              const ratingColors: Record<string, string> = { green: "#16a34a", orange: "#e67e22", yellow: "#facc15", red: "#dc2626" };
              const pendingFU = fruitFollowups.filter((f: any) => f.status === "pending");
              const unansweredQA = fruitQA.filter((q: any) => !q.response);
              // Show CT reports and meetings in recent notes
              const recentMeetingNotes = fruitTimeline
                .filter((t: any) => t.type === "meeting" || t.type === "report")
                .slice(0, 5);

              return (
                <div>
                  {/* Stats row */}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 16 }}>
                    <div style={{ background: "var(--bg)", borderRadius: 8, padding: "10px 12px", textAlign: "center" }}>
                      <div style={{ fontSize: 11, color: "var(--text-muted)" }}>Attendance</div>
                      <div style={{ fontSize: 20, fontWeight: 700, fontFamily: "'Fraunces', serif" }}>{fruit?.attendance_pct || 0}%</div>
                      <div style={{ fontSize: 10, color: "var(--text-muted)" }}>{fruit?.attended || 0}/{fruit?.total_sessions || 0}</div>
                    </div>
                    <div style={{ background: "var(--bg)", borderRadius: 8, padding: "10px 12px", textAlign: "center" }}>
                      <div style={{ fontSize: 11, color: "var(--text-muted)" }}>HW Done</div>
                      <div style={{ fontSize: 20, fontWeight: 700, fontFamily: "'Fraunces', serif" }}>{fruit?.hw_done || 0}/{fruit?.hw_total || 0}</div>
                    </div>
                    <div style={{ background: "var(--bg)", borderRadius: 8, padding: "10px 12px", textAlign: "center" }}>
                      <div style={{ fontSize: 11, color: "var(--text-muted)" }}>Last Met</div>
                      <div style={{ fontSize: 14, fontWeight: 600 }}>{fruit?.last_meeting || "Never"}</div>
                    </div>
                  </div>

                  {/* Trend graph */}
                  {fruit?.ratings?.length > 0 && (() => {
                    const leftPad = 24; const rightPad = 15; const topPad = 10; const botPad = 25;
                    const w = Math.max(fruit.ratings.length * 50 + leftPad + rightPad, 250);
                    const h = 120;
                    const chartW = w - leftPad - rightPad;
                    const chartH = h - topPad - botPad;
                    const stepX = chartW / Math.max(fruit.ratings.length - 1, 1);
                    const stepY = chartH / 3;
                    return (
                      <div style={{ marginBottom: 16 }}>
                        <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 4 }}>Lesson Trend</div>
                        <div style={{ overflowX: "auto" }}>
                          <svg width={w} height={h} style={{ display: "block" }}>
                            {[{v:1,c:"#dc2626"},{v:2,c:"#facc15"},{v:3,c:"#e67e22"},{v:4,c:"#16a34a"}].map(({v,c}) => {
                              const y = topPad + chartH - (v-1) * stepY;
                              return <g key={v}><line x1={leftPad} x2={w-rightPad} y1={y} y2={y} stroke="#f0f0f0" strokeWidth={1}/><circle cx={8} cy={y} r={5} fill={c}/></g>;
                            })}
                            <polyline points={fruit.ratings.map((r:any,i:number)=>{const x=leftPad+i*stepX;const y=topPad+chartH-(ratingValues[r.rating]-1)*stepY;return`${x},${y}`;}).join(" ")} fill="none" stroke="var(--accent)" strokeWidth={2.5} strokeLinejoin="round"/>
                            {fruit.ratings.map((r:any,i:number)=>{const x=leftPad+i*stepX;const y=topPad+chartH-(ratingValues[r.rating]-1)*stepY;const label=r.lesson_number?`L${r.lesson_number}`:r.date?.slice(5);return<g key={i}><circle cx={x} cy={y} r={6} fill={ratingColors[r.rating]} stroke="#fff" strokeWidth={2}><title>{`${r.topic||r.date}: ${r.notes||r.rating}`}</title></circle><text x={x} y={h-4} textAnchor="middle" fontSize={9} fill="#999">{label}</text></g>;})}
                          </svg>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Pending follow-ups */}
                  {pendingFU.length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: "var(--amber-text)", marginBottom: 6 }}>Pending Follow-ups ({pendingFU.length})</div>
                      {pendingFU.map((f: any) => (
                        <div key={f.id} style={{ fontSize: 13, padding: "4px 0", borderBottom: "1px solid var(--border-light)" }}>
                          • {f.action} {f.due_date && <span style={{ fontSize: 11, color: "var(--text-muted)" }}>Due: {f.due_date}</span>}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Unanswered questions */}
                  {unansweredQA.length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: "var(--red)", marginBottom: 6 }}>Unanswered Questions ({unansweredQA.length})</div>
                      {unansweredQA.map((q: any) => (
                        <div key={q.id} style={{ fontSize: 13, padding: "4px 0", borderBottom: "1px solid var(--border-light)" }}>
                          • {q.question}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Recent meeting notes */}
                  {recentMeetingNotes.length > 0 && (
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-secondary)", marginBottom: 6 }}>Recent Notes</div>
                      {recentMeetingNotes.map((m: any) => (
                        <div key={`${m.type}-${m.id}`} style={{ borderLeft: `3px solid ${m.type === "report" ? "#8b5cf6" : "var(--accent)"}`, padding: "6px 10px", marginBottom: 8, fontSize: 12 }}>
                          <div style={{ fontWeight: 600 }}>
                            {m.date}
                            <span style={{ fontWeight: 400, color: m.type === "report" ? "#8b5cf6" : "var(--text-muted)", marginLeft: 6 }}>{m.title}</span>
                          </div>
                          {m.detail && <div style={{ marginTop: 2, color: "var(--text-secondary)", wordBreak: "break-word" }}>{m.detail}</div>}
                        </div>
                      ))}
                    </div>
                  )}

                  {!fruit?.ratings?.length && pendingFU.length === 0 && unansweredQA.length === 0 && recentMeetingNotes.length === 0 && (
                    <div style={{ color: "var(--text-muted)", fontSize: 13, textAlign: "center", padding: 24 }}>No data yet. Add meetings, follow-ups, and lesson reports to build this dashboard.</div>
                  )}
                </div>
              );
            })()}

            {fruitTab === "meetings" && (
              <>
                {fruitMeetings.length === 0 && <div style={{ color: "var(--text-muted)", fontSize: 13, marginBottom: 12 }}>No meetings yet</div>}
                {fruitMeetings.map((m: any) => (
                  <div key={m.id} style={{ borderLeft: "3px solid var(--accent)", padding: "8px 12px", marginBottom: 10, fontSize: 13 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                      <input type="date" defaultValue={m.date} style={{ fontWeight: 600, fontSize: 13, border: "none", background: "transparent", padding: 0, fontFamily: "inherit" }}
                        onBlur={async (e) => { if (e.target.value !== m.date) { await api.put(`/fruit/${fruitModal.id}/meetings/${m.id}`, { date: e.target.value, attendees: m.attendees, notes: m.notes }); const r = await api.get(`/fruit/${fruitModal.id}/meetings`); setFruitMeetings(r.data); loadFruit(); }}} />
                      <button style={{ background: "none", border: "none", color: "var(--red)", cursor: "pointer", fontSize: 14 }} onClick={async () => { await api.delete(`/fruit/${fruitModal.id}/meetings/${m.id}`); setFruitMeetings(fruitMeetings.filter((x: any) => x.id !== m.id)); loadFruit(); }}>&times;</button>
                    </div>
                    <input defaultValue={m.attendees || ""} placeholder="Attendees" style={{ fontSize: 11, color: "var(--text-secondary)", border: "none", background: "transparent", padding: 0, width: "100%", fontFamily: "inherit", marginBottom: 2 }}
                      onBlur={async (e) => { if (e.target.value !== (m.attendees || "")) { await api.put(`/fruit/${fruitModal.id}/meetings/${m.id}`, { date: m.date, attendees: e.target.value, notes: m.notes }); const r = await api.get(`/fruit/${fruitModal.id}/meetings`); setFruitMeetings(r.data); }}} />
                    <textarea defaultValue={m.notes || ""} placeholder="Notes..." style={{ marginTop: 2, wordBreak: "break-word", width: "100%", border: "none", background: "transparent", padding: 0, fontSize: 13, fontFamily: "inherit", resize: "vertical", minHeight: 20 }}
                      onBlur={async (e) => { if (e.target.value !== (m.notes || "")) { await api.put(`/fruit/${fruitModal.id}/meetings/${m.id}`, { date: m.date, attendees: m.attendees, notes: e.target.value }); const r = await api.get(`/fruit/${fruitModal.id}/meetings`); setFruitMeetings(r.data); }}} />
                  </div>
                ))}
                {fruitForm._showAdd ? (
                  <div style={{ borderTop: "1px solid var(--border-light)", paddingTop: 10, marginTop: 8 }}>
                    <div style={{ display: "flex", gap: 8, marginBottom: 6 }}>
                      <input type="date" value={fruitForm.date || new Date().toISOString().split("T")[0]} onChange={(e) => setFruitForm({ ...fruitForm, date: e.target.value })} style={{ flex: 1, padding: "6px 8px", fontSize: 12, border: "1px solid var(--border)", borderRadius: 6 }} />
                      <input placeholder="Attendees" value={fruitForm.attendees || ""} onChange={(e) => setFruitForm({ ...fruitForm, attendees: e.target.value })} style={{ flex: 1, padding: "6px 8px", fontSize: 12, border: "1px solid var(--border)", borderRadius: 6 }} />
                    </div>
                    <textarea placeholder="Notes..." value={fruitForm.notes || ""} onChange={(e) => setFruitForm({ ...fruitForm, notes: e.target.value })} style={{ width: "100%", padding: "6px 8px", fontSize: 12, border: "1px solid var(--border)", borderRadius: 6, minHeight: 50, fontFamily: "inherit", resize: "vertical", marginBottom: 6 }} />
                    <div style={{ display: "flex", gap: 6 }}>
                      <button className="btn btn-primary btn-sm" onClick={async () => {
                        await api.post(`/fruit/${fruitModal.id}/meetings`, { date: fruitForm.date || new Date().toISOString().split("T")[0], attendees: fruitForm.attendees, notes: fruitForm.notes });
                        const r = await api.get(`/fruit/${fruitModal.id}/meetings`); setFruitMeetings(r.data);
                        setFruitForm({}); loadFruit();
                      }}>Save</button>
                      <button className="btn btn-secondary btn-sm" onClick={() => setFruitForm({})}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <button className="btn btn-secondary btn-sm" onClick={() => setFruitForm({ _showAdd: true, date: new Date().toISOString().split("T")[0] })}>+ Add Meeting</button>
                )}
              </>
            )}

            {fruitTab === "followups" && (
              <>
                {fruitFollowups.length === 0 && <div style={{ color: "var(--text-muted)", fontSize: 13, marginBottom: 12 }}>No follow-ups</div>}
                {fruitFollowups.map((f: any) => (
                  <div key={f.id} style={{ padding: "8px 0", borderBottom: "1px solid var(--border-light)", fontSize: 13 }}>
                    <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 4 }}>
                      <input defaultValue={f.action} style={{ flex: 1, fontWeight: 500, fontSize: 13, border: "none", background: "transparent", padding: 0, fontFamily: "inherit" }}
                        onBlur={async (e) => { if (e.target.value !== f.action) { await api.patch(`/fruit/${fruitModal.id}/followups/${f.id}`, { notes: f.notes, status: f.status }); const r = await api.get(`/fruit/${fruitModal.id}/followups`); setFruitFollowups(r.data); }}}
                        onChange={(e) => { f.action = e.target.value; }}
                      />
                      <span className={`badge ${f.status}`} style={{ fontSize: 11 }}>{f.status}</span>
                      {f.status === "pending" && <button className="btn btn-secondary btn-sm" onClick={async () => {
                        await api.patch(`/fruit/${fruitModal.id}/followups/${f.id}`, { status: "resolved" });
                        setFruitFollowups(fruitFollowups.map((x: any) => x.id === f.id ? { ...x, status: "resolved" } : x)); loadFruit();
                      }}>Done</button>}
                      <button style={{ background: "none", border: "none", color: "var(--red)", cursor: "pointer", fontSize: 14 }} onClick={async () => {
                        await api.delete(`/fruit/${fruitModal.id}/followups/${f.id}`);
                        setFruitFollowups(fruitFollowups.filter((x: any) => x.id !== f.id)); loadFruit();
                      }}>&times;</button>
                    </div>
                    <input type="date" defaultValue={f.due_date || ""} style={{ fontSize: 11, color: "var(--text-muted)", border: "none", background: "transparent", padding: 0, fontFamily: "inherit" }} />
                  </div>
                ))}
                {fruitForm._showFU ? (
                  <div style={{ paddingTop: 10, marginTop: 8 }}>
                    <input placeholder="Action item..." value={fruitForm.action || ""} onChange={(e) => setFruitForm({ ...fruitForm, action: e.target.value })} style={{ width: "100%", padding: "6px 8px", fontSize: 12, border: "1px solid var(--border)", borderRadius: 6, marginBottom: 6 }} />
                    <div style={{ display: "flex", gap: 6 }}>
                      <input type="date" value={fruitForm.fu_due || ""} onChange={(e) => setFruitForm({ ...fruitForm, fu_due: e.target.value })} style={{ padding: "6px 8px", fontSize: 12, border: "1px solid var(--border)", borderRadius: 6 }} />
                      <button className="btn btn-primary btn-sm" onClick={async () => {
                        await api.post(`/fruit/${fruitModal.id}/followups`, { action: fruitForm.action, due_date: fruitForm.fu_due });
                        const r = await api.get(`/fruit/${fruitModal.id}/followups`); setFruitFollowups(r.data);
                        setFruitForm({}); loadFruit();
                      }}>Save</button>
                      <button className="btn btn-secondary btn-sm" onClick={() => setFruitForm({})}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <button className="btn btn-secondary btn-sm" style={{ marginTop: 10 }} onClick={() => setFruitForm({ _showFU: true })}>+ Add Follow-up</button>
                )}
              </>
            )}

            {fruitTab === "qa" && (
              <>
                {fruitQA.length === 0 && <div style={{ color: "var(--text-muted)", fontSize: 13, marginBottom: 12 }}>No questions recorded</div>}
                {fruitQA.map((q: any) => (
                  <div key={q.id} style={{ marginBottom: 10, fontSize: 13, borderBottom: "1px solid var(--border-light)", paddingBottom: 8, position: "relative", opacity: q.addressed ? 0.6 : 1 }}>
                    <div style={{ position: "absolute", top: 0, right: 0, display: "flex", gap: 4, alignItems: "center" }}>
                      {q.source === "homework" && <span style={{ fontSize: 9, background: "var(--green-light)", color: "var(--green-text)", padding: "1px 6px", borderRadius: 4 }}>HW</span>}
                      <button
                        style={{ background: "none", border: "none", cursor: "pointer", fontSize: 11, color: q.addressed ? "var(--green-text)" : "var(--amber-text)", fontWeight: 600 }}
                        onClick={async () => {
                          await api.patch(`/fruit/questions/${q.id}/${q.addressed ? "unaddress" : "address"}`);
                          const r = await api.get(`/fruit/${fruitModal.id}/qa`); setFruitQA(r.data);
                        }}
                      >{q.addressed ? "✅ Addressed" : "⏳ Pending"}</button>
                      <button style={{ background: "none", border: "none", color: "var(--red)", cursor: "pointer", fontSize: 14 }} onClick={async () => {
                        await api.delete(`/fruit/${fruitModal.id}/qa/${q.id}`);
                        setFruitQA(fruitQA.filter((x: any) => x.id !== q.id));
                      }}>&times;</button>
                    </div>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 4, paddingRight: 100 }}>
                      <span style={{ fontWeight: 600, color: "var(--text-muted)", fontSize: 12 }}>Q:</span>
                      <textarea defaultValue={q.question} style={{ flex: 1, fontWeight: 600, fontSize: 13, border: "none", background: "transparent", padding: 0, fontFamily: "inherit", resize: "vertical", minHeight: 20 }}
                        onBlur={async (e) => { if (e.target.value !== q.question) { await api.put(`/fruit/${fruitModal.id}/qa/${q.id}`, { question: e.target.value, response: q.response, asked_date: q.asked_date }); const r = await api.get(`/fruit/${fruitModal.id}/qa`); setFruitQA(r.data); }}} />
                    </div>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 4, marginTop: 4 }}>
                      <span style={{ fontSize: 12, color: "var(--accent-text)" }}>A:</span>
                      <textarea defaultValue={q.response || ""} placeholder="Type response..." style={{ flex: 1, fontSize: 12, border: "none", background: q.response ? "var(--accent-light)" : "transparent", borderRadius: 4, padding: q.response ? "4px 6px" : 0, fontFamily: "inherit", resize: "vertical", minHeight: 20, color: "var(--accent-text)" }}
                        onBlur={async (e) => { if (e.target.value !== (q.response || "")) { await api.put(`/fruit/${fruitModal.id}/qa/${q.id}`, { question: q.question, response: e.target.value, asked_date: q.asked_date }); const r = await api.get(`/fruit/${fruitModal.id}/qa`); setFruitQA(r.data); }}} />
                    </div>
                  </div>
                ))}
                {fruitForm._showQA ? (
                  <div style={{ paddingTop: 10, marginTop: 8, borderTop: "1px solid var(--border-light)" }}>
                    <textarea placeholder="Question..." value={fruitForm.question || ""} onChange={(e) => setFruitForm({ ...fruitForm, question: e.target.value })} style={{ width: "100%", padding: "6px 8px", fontSize: 12, border: "1px solid var(--border)", borderRadius: 6, minHeight: 40, fontFamily: "inherit", marginBottom: 6 }} />
                    <textarea placeholder="Response (optional)" value={fruitForm.response || ""} onChange={(e) => setFruitForm({ ...fruitForm, response: e.target.value })} style={{ width: "100%", padding: "6px 8px", fontSize: 12, border: "1px solid var(--border)", borderRadius: 6, minHeight: 40, fontFamily: "inherit", marginBottom: 6 }} />
                    <div style={{ display: "flex", gap: 6 }}>
                      <button className="btn btn-primary btn-sm" onClick={async () => {
                        await api.post(`/fruit/${fruitModal.id}/qa`, { question: fruitForm.question, response: fruitForm.response, asked_date: new Date().toISOString().split("T")[0] });
                        const r = await api.get(`/fruit/${fruitModal.id}/qa`); setFruitQA(r.data);
                        setFruitForm({});
                      }}>Save</button>
                      <button className="btn btn-secondary btn-sm" onClick={() => setFruitForm({})}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <button className="btn btn-secondary btn-sm" style={{ marginTop: 10 }} onClick={() => setFruitForm({ _showQA: true })}>+ Add Q&A</button>
                )}
              </>
            )}

            {fruitTab === "timeline" && (() => {
              const typeColors: Record<string, string> = { meeting: "var(--accent)", report: "#8b5cf6", followup: "#f59e0b", qa: "#6366f1", review: "#ec4899", homework: "#16a34a" };
              const typeLabels: Record<string, string> = { meeting: "Meeting", report: "CT Report", followup: "Follow-up", qa: "Q&A", review: "Review", homework: "Homework" };
              const filtered = fruitTimeline
                .filter((t: any) => timelineFilter === "all" || t.type === timelineFilter)
                .sort((a: any, b: any) => {
                  const da = a.created_at || a.date || "";
                  const db = b.created_at || b.date || "";
                  return timelineSort === "newest" ? db.localeCompare(da) : da.localeCompare(db);
                });
              return (
                <>
                  <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap", alignItems: "center" }}>
                    <select value={timelineFilter} onChange={(e) => setTimelineFilter(e.target.value)} style={{ padding: "4px 8px", fontSize: 12, border: "1px solid var(--border)", borderRadius: 6, fontFamily: "inherit" }}>
                      <option value="all">All types</option>
                      <option value="meeting">Meetings</option>
                      <option value="report">CT Reports</option>
                      <option value="followup">Follow-ups</option>
                      <option value="qa">Q&A</option>
                      <option value="review">Reviews</option>
                      <option value="homework">Homework</option>
                    </select>
                    <select value={timelineSort} onChange={(e) => setTimelineSort(e.target.value as any)} style={{ padding: "4px 8px", fontSize: 12, border: "1px solid var(--border)", borderRadius: 6, fontFamily: "inherit" }}>
                      <option value="newest">Newest first</option>
                      <option value="oldest">Oldest first</option>
                    </select>
                    <span style={{ fontSize: 11, color: "var(--text-muted)" }}>{filtered.length} items</span>
                  </div>
                  {filtered.length === 0 && <div style={{ color: "var(--text-muted)", fontSize: 13 }}>No activity recorded</div>}
                  {filtered.map((t: any) => (
                    <div key={`${t.type}-${t.id}`} style={{ borderLeft: `3px solid ${typeColors[t.type] || "#999"}`, padding: "6px 12px", marginBottom: 8, fontSize: 13 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 2 }}>
                        <span style={{ fontSize: 11, fontWeight: 600, color: typeColors[t.type], textTransform: "uppercase" }}>{typeLabels[t.type] || t.type}</span>
                        <span style={{ fontSize: 11, color: "var(--text-muted)" }}>{t.date || t.created_at?.slice(0, 10)}</span>
                      </div>
                      <div style={{ fontWeight: 500 }}>{t.title}</div>
                      {t.detail && <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", marginTop: 2, color: "var(--text-secondary)" }}>{t.detail}</div>}
                      {t.translated && (
                        <div style={{ background: "var(--accent-light)", borderRadius: 6, padding: "4px 8px", marginTop: 4, fontSize: 12, color: "var(--accent-text)" }}>
                          {t.translated}
                        </div>
                      )}
                      {t.meta && <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>{t.meta}</div>}
                    </div>
                  ))}
                </>
              );
            })()}

            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setFruitModal(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ===== QUESTIONS TAB ===== */}
      {tab === "questions" && (
        <div>
          {classQuestions.length === 0 ? (
            <div className="empty">No student questions yet. Questions are auto-extracted from homework submissions.</div>
          ) : (
            <div className="table-container">
              <table>
                <thead><tr><th>Student</th><th>Question</th><th>Source</th><th>Date</th><th>Status</th><th></th></tr></thead>
                <tbody>
                  {classQuestions.map((q: any) => (
                    <tr key={q.id} style={{ opacity: q.addressed ? 0.5 : 1 }}>
                      <td style={{ fontWeight: 500, whiteSpace: "nowrap" }}>{q.student_name}</td>
                      <td style={{ fontSize: 13 }}>{q.question}</td>
                      <td style={{ fontSize: 11, color: "var(--text-muted)" }}>{q.source === "homework" ? "HW" : "Manual"}</td>
                      <td style={{ fontSize: 12, whiteSpace: "nowrap" }}>{q.asked_date || "-"}</td>
                      <td>
                        {q.addressed ? (
                          <span className="badge present" style={{ fontSize: 11 }}>Addressed</span>
                        ) : (
                          <span className="badge pending" style={{ fontSize: 11 }}>Pending</span>
                        )}
                      </td>
                      <td>
                        {q.addressed ? (
                          <button className="btn btn-secondary btn-sm" onClick={async () => {
                            await api.patch(`/fruit/questions/${q.id}/unaddress`);
                            const r = await api.get(`/fruit/questions/class/${id}`); setClassQuestions(r.data);
                          }}>Undo</button>
                        ) : (
                          <button className="btn btn-primary btn-sm" onClick={async () => {
                            await api.patch(`/fruit/questions/${q.id}/address`);
                            const r = await api.get(`/fruit/questions/class/${id}`); setClassQuestions(r.data);
                          }}>Mark Addressed</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ===== SETTINGS TAB ===== */}
      {tab === "settings" && (
        <div style={{ maxWidth: 500 }}>
          <h3 style={{ fontFamily: "'Fraunces', serif", fontSize: 18, marginBottom: 16 }}>Telegram Notifications</h3>
          <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 20 }}>
            1. Add the bot to your Telegram group (with forum topics enabled)<br />
            2. Make sure each student has a forum topic named after them<br />
            3. Enter the Chat ID below and click "Scan Topics"
          </p>

          <div className="form-group">
            <label>Telegram Chat ID</label>
            <div style={{ display: "flex", gap: 8 }}>
              <input
                value={telegramForm.chat_id}
                onChange={(e) => setTelegramForm({ ...telegramForm, chat_id: e.target.value })}
                placeholder="e.g. -1001234567890"
                style={{ flex: 1 }}
              />
              <button
                className="btn btn-primary"
                onClick={async () => {
                  if (!id || !telegramForm.chat_id) return;
                  await updateClass(id, { telegram_chat_id: telegramForm.chat_id });
                  try {
                    const r = await scanTopics(id);
                    const data = r.data as any;
                    setTelegramSaved(true);
                    setTimeout(() => setTelegramSaved(false), 5000);
                    loadClass();
                    alert(
                      `Scan complete!\n\nMatched: ${data.matched.length} students\n` +
                      (data.unmatched_students.length > 0 ? `Unmatched: ${data.unmatched_students.join(", ")}\n` : "") +
                      `Total topics found: ${data.total_topics_found}` +
                      (data.hint ? `\n\n${data.hint}` : "")
                    );
                  } catch (e: any) {
                    alert("Scan failed: " + (e.response?.data?.detail || e.message));
                  }
                }}
              >Scan Topics</button>
            </div>
          </div>

          {telegramSaved && (
            <div style={{ background: "var(--green-light)", color: "var(--green-text)", padding: "10px 14px", borderRadius: 8, fontSize: 13, marginBottom: 16 }}>
              Topics scanned and linked successfully!
            </div>
          )}

          {/* Show current status */}
          <div style={{ marginTop: 20 }}>
            <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 10, color: "var(--text-secondary)" }}>Current Status</h4>
            <div style={{ fontSize: 13, lineHeight: 2 }}>
              <div>Chat ID: <strong>{cls.telegram_chat_id || "Not set"}</strong></div>
              <div>Reminders Topic: <strong>{cls.telegram_reminder_topic_id || "Not found"}</strong></div>
              <div>Review Responses Topic: <strong>{cls.telegram_review_topic_id || "Not found"}</strong></div>
              <div>Centre Reports Topic: <strong>{cls.telegram_reports_topic_id || "Not found"}</strong></div>
            </div>

            <h4 style={{ fontSize: 14, fontWeight: 600, marginTop: 16, marginBottom: 10, color: "var(--text-secondary)" }}>Student Topics</h4>
            <div className="table-container">
              <table>
                <thead><tr><th>Student</th><th>Topic ID</th></tr></thead>
                <tbody>
                  {cls.students?.map((s: any) => (
                    <tr key={s.id}>
                      <td>{s.name}</td>
                      <td>
                        {s.telegram_topic_id ? (
                          <span style={{ color: "var(--green-text)" }}>{s.telegram_topic_id}</span>
                        ) : (
                          <span style={{ color: "var(--red)", fontStyle: "italic" }}>Not linked</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
